import React, { useState, useMemo, useEffect } from 'react';
import useJobs from '../hooks/useJobs';
import useCategories from '../hooks/useCategories';
import useTowns from '../hooks/useTowns';
import JobFilters from '../components/JobFilters';
import JobCard from '../../../dashboards/worker/components/JobCard';
import CardIcon from '../../../shared/components/icons/CardIcon';

const jobImages = [1, 2, 3, 4, 5, 6, 7].map((number) => ({
  src: `/images/${number}.jpg`,
  alt: 'Skilled tradesperson at work in Zambia',
}));
function FeedPage({ clientMode = false }) {
  const { jobs, loading: jobsLoading, error: jobsError } = useJobs();
  const { categories } = useCategories();
  const { towns } = useTowns();

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTown, setSelectedTown] = useState('');
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    const timer = window.setInterval(() => {
      setActiveSlide((currentSlide) => (currentSlide + 1) % jobImages.length);
    }, 10000);

    return () => window.clearInterval(timer);
  }, []);

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const jobCategory = job.skill?.category?.name || job.category || '';
      const matchesCategory = !selectedCategory || jobCategory === selectedCategory;
      const matchesTown = !selectedTown || job.city === selectedTown;
      return matchesCategory && matchesTown;
    });
  }, [jobs, selectedCategory, selectedTown]);

  return (
    <section className="space-y-6">
      <div className="grid gap-6">
        <div className="overflow-hidden rounded-3xl bg-white shadow-sm">
          <div className="grid sm:grid-cols-[minmax(0,1fr)_300px]">
            <div className="p-6 sm:p-8">
              <p className="text-xs font-bold uppercase text-orange-700">Jobs in Zambia</p>
              <h1 className="mt-2 flex items-center gap-2 text-2xl font-bold text-navy sm:text-3xl"><CardIcon name="briefcase" className="h-6 w-6 shrink-0 sm:h-7 sm:w-7" />Find work. Hire workers. Get things done.</h1>
              <p className="mt-2 text-sm leading-6 text-slate-600">Connect with local opportunities and reliable workers, right where you are.</p>
            </div>
            <div
              className="relative aspect-[16/9] overflow-hidden bg-slate-100 sm:aspect-auto sm:min-h-[220px]"
              role="region"
              aria-roledescription="carousel"
              aria-label="Skilled workers in Zambia"
            >
              <div className="absolute inset-0">
                {jobImages.map((image, index) => (
                  <img
                    key={image.src}
                    src={image.src}
                    alt={image.alt}
                    aria-hidden={activeSlide !== index}
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[3000ms] ease-in-out motion-reduce:transition-none feed-hero-image ${activeSlide === index ? 'opacity-100' : 'opacity-0'}`}
                    loading="eager"
                    decoding="async"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <JobFilters
            categories={categories}
            towns={towns}
            selectedCategory={selectedCategory}
            selectedTown={selectedTown}
            onCategoryChange={setSelectedCategory}
            onTownChange={setSelectedTown}
          />
        </div>
      </div>

      {jobsLoading && (
        <div className="rounded-3xl bg-white p-8 text-center text-slate-600 shadow-sm">Loading jobs...</div>
      )}
      {jobsError && (
        <div className="rounded-3xl bg-rose-50 p-8 text-center text-rose-700 shadow-sm">Error: {jobsError}</div>
      )}
      {!jobsLoading && !jobsError && filteredJobs.length === 0 && (
        <div className="rounded-3xl bg-white p-8 text-center text-slate-500 shadow-sm">No jobs found.</div>
      )}
      {!jobsLoading && !jobsError && filteredJobs.length > 0 && (
        <div className="grid gap-4">
          {filteredJobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              linkTo={`${clientMode ? '/client/job' : '/job'}/${job.id}`}
              showActions={!clientMode}
              requireLoginForApply={!clientMode}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default FeedPage;